import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert, Select } from '@mantine/core';

const AdaptivePlyrPlayer = ({ videoId, title = "Course Video", autoplay = false, debug = false }) => {
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentQuality, setCurrentQuality] = useState('auto');
  const [availableQualities, setAvailableQualities] = useState([]);
  const [networkSpeed, setNetworkSpeed] = useState(null);
  const [bufferHealth, setBufferHealth] = useState(100);
  const [autoQualityActive, setAutoQualityActive] = useState('720p');
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  // Quality configurations
  const qualityOptions = [
    { value: 'auto', label: `Auto - ${autoQualityActive}`, bandwidth: 0 },
    { value: '360p', label: '360p (SD)', bandwidth: 896000 },
    { value: '480p', label: '480p (SD)', bandwidth: 1528000 },
    { value: '720p', label: '720p (HD)', bandwidth: 2928000 },
    { value: '1080p', label: '1080p (FHD)', bandwidth: 5192000 },
  ];

  // Construct URLs
  const masterPlaylistUrl = `https://manifestationmagic.samrruddhi.in/adaptivevideo/${videoId}/master.m3u8`;
  const getQualityUrl = (quality) => `https://manifestationmagic.samrruddhi.in/adaptivevideo/${videoId}/${quality}/index.m3u8`;

  // Debug logging function
  const debugLog = (message, data = null) => {
    if (!debug) return; // Only log if debug is enabled
    const timestamp = new Date().toISOString();
    console.log(`[AdaptivePlayer ${timestamp}] ${message}`, data || '');
  };

  // Network speed estimation
  const estimateNetworkSpeed = () => {
    const startTime = performance.now();
    const testImage = new Image();

    testImage.onload = () => {
      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000; // Convert to seconds
      const bits = 50000 * 8; // 50KB test image in bits
      const speedBps = bits / duration;
      const speedKbps = Math.round(speedBps / 1000);

      setNetworkSpeed(speedKbps);
      debugLog('Network speed estimated', `${speedKbps} Kbps`);
    };

    testImage.src = `https://manifestationmagic.samrruddhi.in/videos/test.txt?t=${Date.now()}`;
  };

  // Buffer health monitoring
  const monitorBufferHealth = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    if (video.buffered.length > 0 && video.currentTime > 0) {
      const bufferedEnd = video.buffered.end(video.buffered.length - 1);
      const currentTime = video.currentTime;
      const bufferAhead = bufferedEnd - currentTime;
      const healthPercentage = Math.min(100, Math.max(0, (bufferAhead / 30) * 100)); // 30 seconds = 100%

      setBufferHealth(healthPercentage);
      debugLog('Buffer health', `${healthPercentage.toFixed(1)}% (${bufferAhead.toFixed(1)}s ahead)`);

      return bufferAhead;
    }
    return 0;
  };

  // Auto quality selection based on network conditions
  const selectAutoQuality = () => {
    const bufferAhead = monitorBufferHealth();

    if (!networkSpeed) {
      debugLog('Auto quality selection skipped', 'Network speed not available');
      return '720p'; // Default fallback
    }

    let selectedQuality = '720p';

    // Quality selection logic based on network speed and buffer health
    if (networkSpeed > 4000 && bufferHealth > 50) {
      selectedQuality = '1080p';
    } else if (networkSpeed > 2500 && bufferHealth > 30) {
      selectedQuality = '720p';
    } else if (networkSpeed > 1200 && bufferHealth > 20) {
      selectedQuality = '480p';
    } else {
      selectedQuality = '360p';
    }

    // Downgrade if buffer is low
    if (bufferHealth < 20) {
      if (selectedQuality === '1080p') selectedQuality = '720p';
      else if (selectedQuality === '720p') selectedQuality = '480p';
      else if (selectedQuality === '480p') selectedQuality = '360p';
    }

    debugLog('Auto quality selected', `${selectedQuality} (Speed: ${networkSpeed}Kbps, Buffer: ${bufferHealth.toFixed(1)}%)`);
    return selectedQuality;
  };

  // Switch quality
  const switchQuality = (quality) => {
    if (!hlsRef.current || !videoRef.current) {
      debugLog('Quality switch failed', 'HLS or video not available');
      return;
    }

    const video = videoRef.current;
    const currentTime = video.currentTime;
    const wasPlaying = !video.paused;

    debugLog('Switching quality', `From ${currentQuality} to ${quality} at ${currentTime.toFixed(2)}s`);

    if (quality === 'auto') {
      // Switch to auto mode - use master playlist
      hlsRef.current.loadSource(masterPlaylistUrl);
      setCurrentQuality('auto');
      debugLog('Switched to auto mode', masterPlaylistUrl);
    } else {
      // Switch to specific quality
      const qualityUrl = getQualityUrl(quality);
      hlsRef.current.loadSource(qualityUrl);
      setCurrentQuality(quality);
      debugLog('Switched to manual quality', `${quality} - ${qualityUrl}`);
    }

    // Restore playback position and state
    video.addEventListener('loadedmetadata', function restorePlayback() {
      video.currentTime = currentTime;
      if (wasPlaying) {
        video.play().catch(e => debugLog('Play failed after quality switch', e.message));
      }
      video.removeEventListener('loadedmetadata', restorePlayback);
    });
  };

  // Initialize player
  useEffect(() => {
    const initializePlayer = async () => {
      try {
        debugLog('Initializing player', { videoId, autoplay });
        setIsLoading(true);
        setError(null);

        // Estimate network speed
        estimateNetworkSpeed();

        // Dynamic imports
        const Plyr = (await import('plyr')).default;
        const Hls = (await import('hls.js')).default;

        if (videoRef.current && !playerRef.current) {
          const video = videoRef.current;

          // Initialize HLS
          if (Hls.isSupported()) {
            debugLog('HLS supported, initializing');

            const hls = new Hls({
              enableWorker: true,
              lowLatencyMode: false,
              backBufferLength: 90,
              maxBufferLength: 30,
              debug: true, // Enable HLS debug logs
              abrEwmaDefaultEstimate: 800000, // 3 Mbps - this will favor 720p initially
              // startLevel: -1, // Add this: -1 means auto, but we'll override it after manifest parse
            });

            hlsRef.current = hls;

            // Start with master playlist (auto mode)
            hls.loadSource(masterPlaylistUrl);
            hls.attachMedia(video);

            // HLS event handlers
            hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
              debugLog('HLS manifest parsed', { levels: data.levels.length, qualities: data.levels.map(l => `${l.height}p`) });
              setAvailableQualities(data.levels.map(level => `${level.height}p`));
              setIsLoading(false);
            });


            hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
              const newQuality = hls.levels[data.level];
              const qualityName = `${newQuality.height}p`;
              debugLog('HLS level switched', `Auto switched to ${qualityName} (${newQuality.bitrate} bps)`);

              if (currentQuality === 'auto') {
                setAutoQualityActive(qualityName);
              }
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
              debugLog('HLS Error', { type: data.type, details: data.details, fatal: data.fatal });

              if (data.fatal) {
                setError(`Video loading failed: ${data.details}`);
                setIsLoading(false);
              }
            });

            hls.on(Hls.Events.BUFFER_APPENDED, () => {
              monitorBufferHealth();
            });

          } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            debugLog('Native HLS support detected');
            video.src = masterPlaylistUrl;
            setIsLoading(false);
          } else {
            debugLog('HLS not supported');
            setError('HLS streaming not supported on this browser');
            setIsLoading(false);
            return;
          }

          // Detect if device is mobile
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          debugLog('Device detection', isMobile ? 'Mobile' : 'Desktop');

          // Initialize Plyr with quality settings
          const player = new Plyr(video, {
            controls: isMobile ? [
              'play-large', 'play', 'progress', 'current-time', 'duration', 'settings', 'fullscreen'
            ] : [
              'play-large', 'play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'settings', 'fullscreen'
            ],
            settings: ['speed'],
            speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },

            autoplay: autoplay,
            hideControls: true,
            resetOnEnd: false,
            keyboard: { focused: true, global: false },
            tooltips: { controls: true, seek: true },
            customControls: {
              left: ['play', 'progress', 'current-time', 'duration'],
              middle: [],
              right: isMobile ? ['quality-custom', 'settings', 'fullscreen'] : ['mute', 'volume', 'quality-custom', 'settings', 'fullscreen']
            },
            displayDuration: true,
            storage: { enabled: false },
            fullscreen: {
              enabled: true,
              fallback: true,
              iosNative: true,
            }
          });

          playerRef.current = player;

          // Player event handlers
          player.on('ready', () => {
            debugLog('Plyr player ready');

            // Set initial quality to 720p for auto mode
            setAutoQualityActive('720p');


            /* !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */

            // Create custom quality control
            setTimeout(() => {
              const controls = player.elements.controls;
              if (!controls) return;

              // Find the right control group (where volume/settings are)
              const settingsButton = controls.querySelector('[data-plyr="settings"]');
              if (!settingsButton) return;

              // Create quality button
              const qualityButton = document.createElement('button');
              qualityButton.type = 'button';
              qualityButton.className = 'plyr__control';
              qualityButton.setAttribute('data-plyr', 'quality');
              qualityButton.innerHTML = `
    <span class="plyr__control__text">HD</span>
    <span class="plyr__tooltip" role="tooltip">Quality</span>
  `;

              // Create quality menu container
              const qualityMenuContainer = document.createElement('div');
              qualityMenuContainer.style.position = 'relative';
              qualityMenuContainer.style.display = 'inline-flex';

              // Create quality menu
              const qualityMenu = document.createElement('div');
              qualityMenu.className = 'plyr__menu plyr__menu--quality';
              qualityMenu.style.display = 'none';
              qualityMenu.style.position = 'absolute';
              qualityMenu.style.bottom = '48px';
              qualityMenu.style.right = '0';
              qualityMenu.innerHTML = `
    <div>
      ${['auto', '1080p', '720p', '480p', '360p'].map(q => `
        <button type="button" class="plyr__control plyr__menu__item" data-quality="${q}" role="menuitemradio">
          <span>${q === 'auto' ? `Auto` : q}</span>
          
        </button>
      `).join('')}
    </div>
  `;

              /*               qualityMenu.innerHTML = `
                <div>
                  ${['auto', '1080p', '720p', '480p', '360p'].map(q => `
                    <button type="button" class="plyr__control plyr__menu__item" data-quality="${q}" role="menuitemradio" aria-checked="${currentQuality === q}">
                      <span>${q === 'auto' ? `Auto` : q}</span>
                      ${currentQuality === q ? '<span class="plyr__menu__value">✓</span>' : ''}
                    </button>
                  `).join('')}
                </div>
              `; */

              // Assemble the quality control
              qualityMenuContainer.appendChild(qualityButton);
              qualityMenuContainer.appendChild(qualityMenu);

              // Insert directly before settings button
              settingsButton.parentNode.insertBefore(qualityMenuContainer, settingsButton);

              // Get button text reference
              const buttonText = qualityButton.querySelector('.plyr__control__text');

              // Update button text function
              const updateButtonText = () => {
                if (currentQuality === 'auto') {
                  buttonText.textContent = autoQualityActive;
                } else {
                  buttonText.textContent = currentQuality;
                }
              };

              // Toggle menu handler
              qualityButton.addEventListener('click', (e) => {
                e.stopPropagation();
                const isOpen = qualityMenu.style.display === 'block';
                qualityMenu.style.display = isOpen ? 'none' : 'block';
                qualityButton.classList.toggle('plyr__control--pressed', !isOpen);

                if (!isOpen) {
                  // Update menu items
                  qualityMenu.querySelectorAll('[data-quality]').forEach(item => {
                    const q = item.getAttribute('data-quality');
                    const isActive = q === currentQuality;
                    //item.setAttribute('aria-checked', isActive);

                    // Update text
                    /* if (q === 'auto') {
                      item.querySelector('span').textContent = `Auto (${autoQualityActive})`;
                    } */

                    // Update checkmark
                    const existingCheckmark = item.querySelector('.plyr__menu__value');
                    if (isActive && !existingCheckmark) {
                      // item.innerHTML += '<span class="plyr__menu__value">✓</span>';
                    } else if (!isActive && existingCheckmark) {
                      existingCheckmark.remove();
                    }
                  });
                }
              });

              // Handle quality selection
              qualityMenu.addEventListener('click', (e) => {
                e.stopPropagation();
                const item = e.target.closest('[data-quality]');
                if (item) {
                  const quality = item.getAttribute('data-quality');
                  debugLog('Quality changed via custom control', quality);
                  switchQuality(quality);
                  qualityMenu.style.display = 'none';
                  qualityButton.classList.remove('plyr__control--pressed');
                  updateButtonText();
                }
              });

              // Close menu when clicking elsewhere
              document.addEventListener('click', (e) => {
                if (!qualityMenuContainer.contains(e.target)) {
                  qualityMenu.style.display = 'none';
                  qualityButton.classList.remove('plyr__control--pressed');
                }
              });

              // Store update function globally for use elsewhere
              window.updateQualityButtonText = updateButtonText;

              // Initial update
              updateButtonText();
            }, 100); // Small delay to ensure DOM is ready

            /* @@@!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */




            // Monitor controls visibility
            const controlsElement = player.elements.controls;
            if (controlsElement) {
              const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                  if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const isHidden = controlsElement.classList.contains('plyr__controls--hidden');
                    setControlsVisible(!isHidden);
                  }
                });
              });

              observer.observe(controlsElement, {
                attributes: true,
                attributeFilter: ['class']
              });
            }

            // Auto-rotate on mobile fullscreen
            if (isMobile && 'screen' in window && 'orientation' in window.screen) {
              player.on('enterfullscreen', () => {
                debugLog('Entering fullscreen on mobile');
                if (window.screen.orientation && window.screen.orientation.lock) {
                  window.screen.orientation.lock('landscape').catch(err => {
                    debugLog('Screen orientation lock failed', err.message);
                  });
                }
              });

              player.on('exitfullscreen', () => {
                debugLog('Exiting fullscreen on mobile');
                if (window.screen.orientation && window.screen.orientation.unlock) {
                  window.screen.orientation.unlock();
                }
              });
            }
          });

          player.on('loadstart', () => debugLog('Video loading started'));
          player.on('waiting', () => {
            debugLog('Video buffering/waiting');
            if (player.elements.container) {
              player.elements.container.classList.add('plyr--custom-loading');
            }
          });
          player.on('canplay', () => {
            debugLog('Video can play');
            if (player.elements.container) {
              player.elements.container.classList.remove('plyr--custom-loading');
            }
          });
          player.on('playing', () => {
            debugLog('Video playing');
            setIsPlaying(true);
            if (player.elements.container) {
              player.elements.container.classList.remove('plyr--custom-loading');
            }
          });

          player.on('pause', () => {
            debugLog('Video paused');
            setIsPlaying(false);
          });
          player.on('stalled', () => {
            debugLog('Video stalled');
            if (player.elements.container) {
              player.elements.container.classList.add('plyr--custom-loading');
            }
          });

          player.on('error', (event) => {
            debugLog('Plyr error', event.detail);
            setError('Video playback error occurred');
          });
        }
      } catch (err) {
        debugLog('Player initialization error', err.message);
        setError('Failed to initialize video player');
        setIsLoading(false);
      }
    };

    initializePlayer();

    // Set up periodic buffer monitoring
    const bufferMonitor = setInterval(() => {
      if (currentQuality === 'auto') {
        const newAutoQuality = selectAutoQuality();
        if (newAutoQuality !== autoQualityActive) {
          debugLog('Auto quality change needed', `${autoQualityActive} → ${newAutoQuality}`);
        }
      }
    }, 5000);

    return () => {
      clearInterval(bufferMonitor);
      if (playerRef.current) {
        debugLog('Cleaning up player');
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (hlsRef.current) {
        debugLog('Cleaning up HLS');
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoId, autoplay]);


  // Update quality menu display
  /*   const updateQualityMenuDisplay = (player, quality) => {
      try {
        // Update the main settings button
        const qualityValue = player.elements.container.querySelector('[data-plyr="quality"] .plyr__menu__value');
        if (qualityValue) {
          qualityValue.textContent = quality === 'auto' ? `Auto - ${autoQualityActive}` : quality;
        }
  
        // Update active state in menu
        const qualityButtons = player.elements.container.querySelectorAll('[data-quality]');
        qualityButtons.forEach(button => {
          const isActive = button.getAttribute('data-quality') === quality;
          button.setAttribute('aria-checked', isActive);
          if (isActive) {
            button.classList.add('plyr__control--active');
          } else {
            button.classList.remove('plyr__control--active');
          }
        });
  
        // Update auto quality display if needed
        if (quality === 'auto') {
          const autoButton = player.elements.container.querySelector('[data-quality="auto"] span');
          if (autoButton) {
            autoButton.textContent = `Auto - ${autoQualityActive}`;
          }
        }
      } catch (error) {
        debugLog('Error updating quality menu display', error.message);
      };
    }; */

  // Update quality dropdown label for auto mode
  const getQualityOptions = () => {
    return qualityOptions.map(option => {
      if (option.value === 'auto') {
        return { ...option, label: `Auto - ${autoQualityActive}` };
      }
      return option;
    });
  };

  // Update auto quality display in settings menu
  /*   useEffect(() => {
      if (playerRef.current && currentQuality === 'auto') {
        updateQualityMenuDisplay(playerRef.current, 'auto');
      }
    }, [autoQualityActive]); */

  // Update quality button text when auto quality changes
  useEffect(() => {
    //alert('Auto quality updated: ' + autoQualityActive + ' - ' + (window.updateQualityButtonText ? 'exists' : 'not exists'));
    if (window.updateQualityButtonText) {
      window.updateQualityButtonText();
    }

    const buttonText = document.querySelector('.plyr__control__text');


    if (buttonText) {
      if (autoQualityActive !== '0p' || currentQuality !== 'auto') {
        buttonText.textContent = currentQuality === 'auto' ? `Auto (${autoQualityActive})` : currentQuality;
      }
      else {
        buttonText.textContent = 'Loading...';
      }
    }
  }, [autoQualityActive, currentQuality]);


  if (error) {
    return (
      <Alert color="red" title="Video Error">
        {error}
      </Alert>
    );
  }

  return (
    <Box style={{ width: '100%', position: 'relative' }}>
      {/* Plyr CSS and Custom Styling */}
      <style jsx>{`
        @import url('https://cdn.plyr.io/3.7.8/plyr.css');




        .plyr__control[data-plyr="quality"] {
          background: rgba(114, 67, 236, 0.6);
          border-radius: 3px;
          padding: 7px 10px;
          transition: background 0.2s;
        }

        .plyr__control[data-plyr="quality"]:hover {
          background: rgba(114, 67, 236, 0.1);
        }





        /* Custom theme with your accent color #7243ec */
        .plyr {
          border-radius: 8px;
          overflow: hidden;
        }

        .plyr--video {
          background: #000;
        }

        .plyr__control--overlaid {
          background: rgba(114, 67, 236, 0.9) !important;
          border: 2px solid #7243ec !important;
          border-radius: 50% !important;
        }

        .plyr__control--overlaid:hover {
          background: rgba(114, 67, 236, 1) !important;
        }

        .plyr__progress__buffer {
          background: rgba(255, 255, 255, 0.25) !important;
        }

        .plyr__progress--seek {
          background: rgba(255, 255, 255, 0.3) !important;
        }

        .plyr__progress--played {
          background: #7243ec !important;
        }

        .plyr__volume--display {
          background: #7243ec !important;
        }

        .plyr__control svg {
          fill: white !important;
        }

        .plyr__control:hover {
          background: rgba(114, 67, 236, 0.8) !important;
        }

        .plyr__control[aria-pressed="true"] {
          background: rgba(114, 67, 236, 0.9) !important;
          color: white !important;
        }

        .plyr__time {
          color: rgba(255, 255, 255, 0.9) !important;
          font-size: 14px !important;
        }

        .plyr__controls {
          background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.8)) !important;
          border-radius: 0 !important;
          padding: 15px 15px 10px 15px !important;
        }

        .plyr__progress {
          height: 8px !important;
          margin-bottom: 10px !important;
        }

        .plyr__progress input[type="range"] {
          height: 8px !important;
        }

        .plyr__progress__thumb {
          background: #7243ec !important;
          box-shadow: 0 1px 1px rgba(114, 67, 236, 0.15), 0 0 0 1px rgba(114, 67, 236, 0.2) !important;
        }

        /* Loading/Buffering Animation */
        .plyr--custom-loading .plyr__poster::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 60px;
          height: 60px;
          margin: -30px 0 0 -30px;
          border: 4px solid rgba(255, 255, 255, 0.2);
          border-top: 4px solid #7243ec;
          border-radius: 50%;
          animation: plyr-loading-spin 1s linear infinite;
          z-index: 10;
        }

        @keyframes plyr-loading-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .plyr--custom-loading .plyr__poster::before {
          content: 'Buffering...';
          position: absolute;
          top: calc(50% + 45px);
          left: 50%;
          transform: translateX(-50%);
          color: white;
          font-size: 16px;
          font-weight: 500;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
          z-index: 11;
        }

        .plyr--custom-loading .plyr__poster {
          display: block !important;
          background-color: rgba(0, 0, 0, 0.7) !important;
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .plyr__controls {
            padding: 15px 10px 10px 10px !important;
          }

          .plyr__control {
            padding: 12px !important;
            margin: 0 2px !important;
          }

          .plyr__progress {
            height: 10px !important;
            margin-bottom: 12px !important;
          }

          .plyr__progress input[type="range"] {
            height: 10px !important;
          }

          .plyr__time {
            font-size: 16px !important;
          }

          .plyr__control--overlaid {
            padding: 15px !important;
          }

          .plyr__volume {
            display: none !important;
          }

          .plyr__controls .plyr__control[data-plyr="mute"] {
            display: none !important;
          }

          .plyr--custom-loading .plyr__poster::after {
            width: 50px;
            height: 50px;
            margin: -25px 0 0 -25px;
            border-width: 3px;
          }

          .plyr--custom-loading .plyr__poster::before {
            top: calc(50% + 35px);
            font-size: 14px;
          }
        }

        /* Fullscreen adjustments */
        .plyr--fullscreen-active {
          background: #000 !important;
        }

        /* Quality dropdown z-index fix - No longer needed */
        /* Hide quality dropdown when controls are hidden - No longer needed */








        /* Custom quality control styling */
.plyr__control--quality {
  position: relative;
}

.plyr__control--quality .plyr__control__text {
  font-size: 13px;
  font-weight: 600;
  min-width: 35px;
  display: inline-block;
  text-align: center;
}

.plyr__menu--quality {
  position: absolute;
  bottom: 100%;
  right: -10px;
  margin-bottom: 10px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 4px;
  padding: 4px 0;
  min-width: 140px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  z-index: 20;
}

.plyr__menu--quality button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.plyr__menu--quality button:hover {
  background: rgba(114, 67, 236, 0.8);
  color: white;
}

.plyr__menu--quality button[aria-checked="true"] {
  color: #7243ec;
  font-weight: 600;
}

.plyr__menu--quality .plyr__menu__value {
  margin-left: auto;
  padding-left: 10px;
  color: #7243ec;
}

.plyr__control--pressed {
  background: rgba(114, 67, 236, 0.9) !important;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .plyr__control--quality .plyr__control__text {
    font-size: 14px;
  }
  
  .plyr__menu--quality {
    right: -20px;
    min-width: 120px;
  }
}



/* Adjust quality button text width */
.plyr__control--quality .plyr__control__text {
  font-size: 13px;
  font-weight: 600;
  min-width: 45px; /* Increased from 35px to accommodate "1080p" */
  display: inline-block;
  text-align: center;
}

      `}</style>

      {/* Debug info overlay (visible when debug=true) */}
      {debug && (
        <Box
          style={{
            position: 'absolute',
            bottom: '80px',
            left: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 20,
            fontFamily: 'monospace'
          }}
        >
          <div>Quality: {currentQuality === 'auto' ? `Auto (${autoQualityActive})` : currentQuality}</div>
          <div>Network: {networkSpeed ? `${networkSpeed} Kbps` : 'Testing...'}</div>
          <div>Buffer: {bufferHealth.toFixed(1)}%</div>
        </Box>
      )}

      {/* Title overlay */}
      {title && (
        <Box
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            right: '180px',
            color: 'white',
            fontSize: '18px',
            fontWeight: 600,
            textShadow: '0 2px 8px rgba(0,0,0,0.8)',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        >
          {title}
        </Box>
      )}

      {/* Video element */}
      <video
        ref={videoRef}
        playsInline
        preload="metadata"
        style={{
          width: '100%',
          aspectRatio: '16/9',
          backgroundColor: '#000'
        }}
        onContextMenu={(e) => e.preventDefault()}
      />
    </Box>
  );
};

export default AdaptivePlyrPlayer;